import { useId, useState } from 'react';
import { api } from '../../lib/api.js';
import IntegrationForm, { TYPE_CONFIG, errorText } from './IntegrationForm.jsx';

// One plain sentence per result class (the API and the sender only ever report these).
const RESULT_TEXT = {
  ok: 'Delivered.',
  auth: 'Teams refused the connection. Create the workflow again and replace the URL.',
  not_found: 'Teams could not find that workflow. It may have been deleted. Create it again and replace the URL.',
  network: 'Teams could not be reached. It will be tried again.',
  rejected: 'Teams did not accept the message. Check that the workflow is turned on.',
  rate_limited: 'Teams is receiving too many messages. It will be tried again.',
  timeout: 'Teams did not answer in time. It will be tried again.',
  failed: 'Some tickets could not be delivered within 24 hours.',
};
const TEST_TEXT = {
  ok: 'The test message was delivered.',
  auth: RESULT_TEXT.auth,
  not_found: RESULT_TEXT.not_found,
  network: 'Teams could not be reached. Try again in a few minutes.',
  rejected: RESULT_TEXT.rejected,
  rate_limited: 'Teams is receiving too many messages. Try again in a few minutes.',
  timeout: 'Teams did not answer in time. Try again in a few minutes.',
};
const STATE_TEXT = { untested: 'Not tested yet', ok: 'Working', error: 'Needs attention' };
const when = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(); };

/**
 * Integrations (ENTRAID-340 phase 4): where a Watch "No" answer becomes a ticket. Teams first. The tab exists only while the stack
 * feature IntegrationsEnabled is on (every settings answer says so, and the page does not list the tab otherwise). Admin only; an
 * operator sees the rows and no control. A secret (the webhook URL) is entered here once, is never prefilled and is never shown
 * again: the page never displays a webhook URL, token or host.
 *
 * @param {object} props
 * @param {string} props.customerId
 * @param {object} props.view the settings view: integrations (id, type, label, state, lastResult, lastResultAt) and integrationTypes
 * @param {boolean} props.readOnly an operator who is not this tenant's admin
 * @param {(view: object) => void} props.onChange receives the reloaded settings view after a change
 */
export default function IntegrationsTab({ customerId, view, readOnly, onChange }) {
  const id = useId();
  const rows = Array.isArray(view?.integrations) ? view.integrations : [];
  const types = (Array.isArray(view?.integrationTypes) ? view.integrationTypes : []).filter((t) => TYPE_CONFIG[t]);
  const [adding, setAdding] = useState('');
  const [replacing, setReplacing] = useState('');
  const [confirming, setConfirming] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notes, setNotes] = useState({});
  const full = rows.length >= 5;

  async function reload() { onChange(await api.companySettings(customerId)); }
  async function run(kind, fn, done) {
    if (busy) return;
    setBusy(kind);
    setError('');
    try {
      await fn();
      if (done) done();
      await reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  }
  async function test(row) {
    if (busy) return;
    setBusy(`test-${row.id}`);
    setError('');
    try {
      const out = await api.testIntegration(customerId, row.id);
      setNotes((n) => ({ ...n, [row.id]: TEST_TEXT[out?.result] ?? TEST_TEXT.network }));
      await reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="space-y-6">
      <section className="card space-y-3" aria-labelledby={`${id}-heading`}>
        <h2 id={`${id}-heading`}>Ticket integrations</h2>
        <p className="text-sm text-slate-700">
          When someone answers No to a notice, onGuard can open a ticket in your own tools. Tickets can take up to 15 minutes to appear.
          The notice email still goes out immediately.
        </p>

        {rows.length === 0 ? <p className="text-sm text-slate-700">No integrations yet.</p> : (
          <ul className="divide-y divide-slate-100">
            {rows.map((r) => (
              <li key={r.id} className="space-y-1 py-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-medium">{r.label || TYPE_CONFIG[r.type]?.label || r.type}</span>
                  <span className="text-xs text-slate-600">{TYPE_CONFIG[r.type]?.label ?? r.type}</span>
                  <span className="text-xs text-slate-600">{STATE_TEXT[r.state] ?? STATE_TEXT.untested}</span>
                </div>
                {r.lastResult ? <p className="text-xs text-slate-600">Last result: {RESULT_TEXT[r.lastResult] ?? RESULT_TEXT.network}{r.lastResultAt ? ` (${when(r.lastResultAt)})` : ''}</p> : null}
                {Number.isInteger(r.sent24h) ? <p className="text-xs text-slate-600">Tickets in the last day: {r.sent24h}</p> : null}
                {notes[r.id] ? <p role="status" className="text-sm text-slate-700">{notes[r.id]}</p> : null}
                {readOnly ? null : (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => test(r)}>Test connection</button>
                    <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => { setReplacing(r.id); setAdding(''); setError(''); }}>Replace the URL</button>
                    {confirming === r.id ? (
                      <>
                        <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => run('delete', () => api.deleteIntegration(customerId, r.id), () => setConfirming(''))}>Confirm delete</button>
                        <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => setConfirming('')}>Keep it</button>
                      </>
                    ) : <button type="button" className="btn-secondary" disabled={busy !== ''} onClick={() => setConfirming(r.id)}>Delete</button>}
                  </div>
                )}
                {replacing === r.id && !readOnly ? (
                  <IntegrationForm
                    type={r.type}
                    rotate
                    busy={busy !== ''}
                    error={error}
                    onCancel={() => { setReplacing(''); setError(''); }}
                    onSubmit={(values) => run('replace', () => api.updateIntegration(customerId, r.id, values), () => setReplacing(''))}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {readOnly ? null : (
          <div className="space-y-3">
            {adding ? (
              <IntegrationForm
                type={adding}
                busy={busy !== ''}
                error={error}
                onCancel={() => { setAdding(''); setError(''); }}
                onSubmit={(body) => run('add', () => api.createIntegration(customerId, body), () => setAdding(''))}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Add</span>
                {types.map((t) => (
                  <button key={t} type="button" className="btn-secondary" disabled={busy !== '' || full} onClick={() => { setAdding(t); setReplacing(''); setError(''); }}>{TYPE_CONFIG[t].addLabel}</button>
                ))}
                {full ? <span className="text-xs text-slate-600">A company can have at most 5 integrations.</span> : null}
              </div>
            )}
            {error && !adding && !replacing ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
          </div>
        )}
      </section>
    </div>
  );
}
